import { ClientProvider } from 'hooks/webapi/useClient';
import { ClientBudgetProvider } from 'hooks/webapi/useClientBudget';
import { ClientHouseholdProvider } from 'hooks/webapi/useClientHousehold';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router';
import * as R from 'Router/routes';
import { Button, Layout } from 'components';
import Notifications from './Notifications/Notifications';
import styles from './Registration.module.scss';
import useError from 'hooks/useError';
import useProgress from 'hooks/useProgress';
import { CalculatorProvider } from 'hooks/calculator/CalculatorProvider';
import ConstraintsProvider from 'hooks/context/Constraints/ConstraintsProvider';
import { ConsentsProvider } from 'hooks/webapi/useConsents';
import { newExternalUrl } from 'utils/urlHelpers';
import { session, useSession } from 'session';

export default function Registration() {
  const { t } = useTranslation();
  const location = useLocation();
  const {
    disabled = false,
    section,
    step,
    sectionList,
    showFooter,
    showProgress,
    withCalculator,
    showSidebar,
    nextStepsDisabled,
    layoutStyles,
  } = useProgress();

  const isAuthModeResolving = useSession((state) => state.mode === null);
  const isAuthenticated = useSession(
    (state) => state.status === 'authenticated',
  );

  const prevStepRef = useRef(-1);
  const { removeError } = useError();

  useEffect(() => {
    window.scrollTo(0, 0);
    removeError();
  }, [location.pathname, removeError]);

  useEffect(() => {
    prevStepRef.current = step;
  });

  const prevStep = prevStepRef.current;

  const handleLogout = useCallback(
    () => session.logout({ redirectTo: newExternalUrl(R.INTRO) }),
    [],
  );

  const renderNavigationItems = useCallback(() => {
    if (!isAuthenticated || location.pathname === R.INTRO) {
      return <div />;
    }

    return (
      <Button
        className={styles['logout-container']}
        dataTest="registration-logout-btn"
        buttonClassName={styles.logout}
        outline
        size="s"
        onClick={handleLogout}
        pending={isAuthModeResolving}
      >
        {t('common:logout')}
      </Button>
    );
  }, [
    handleLogout,
    t,
    location.pathname,
    isAuthModeResolving,
    isAuthenticated,
  ]);

  return (
    <ConstraintsProvider>
      <ClientProvider>
        <ClientHouseholdProvider>
          <ClientBudgetProvider>
            <CalculatorProvider>
              <ConsentsProvider>
                <Layout
                  withCalculator={withCalculator}
                  steps={sectionList}
                  progressDisabled={disabled}
                  currentStep={section}
                  showFooter={showFooter}
                  showProgress={showProgress}
                  transitionDirection={prevStep > step ? 'top' : 'bottom'}
                  url={window.location.href}
                  renderNavigationItems={renderNavigationItems}
                  showSidebar={showSidebar}
                  nextStepsDisabled={nextStepsDisabled}
                  layoutStyles={layoutStyles}
                >
                  <Notifications />
                  <Outlet />
                </Layout>
              </ConsentsProvider>
            </CalculatorProvider>
          </ClientBudgetProvider>
        </ClientHouseholdProvider>
      </ClientProvider>
    </ConstraintsProvider>
  );
}
